import dynamic from 'next/dynamic';
import { useEffect, useLayoutEffect } from 'react';

import {
  ColorModeScript,
  ThemeProvider,
  ToastProvider,
  createLocalStorageManager,
} from '@vimeo/bokeh';
import { bokehTheme } from '@vimeo/bokeh-theme';

import { useIsBokeh } from '../hooks/use-is-bokeh';

export const STORAGE_KEY = 'bokeh-color-mode';

const BokehFont = dynamic(() => import('./BokehFont'));

const useIsomorphicEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const BokehProvider = ({
  children,
  colorMode,
  bokehStorageKey = STORAGE_KEY,
}: React.PropsWithChildren<{
  colorMode: 'system' | 'light' | 'dark';
  bokehStorageKey?: string;
}>) => {
  const manager = createLocalStorageManager(bokehStorageKey);

  useIsomorphicEffect(() => {
    manager.set(colorMode);
  }, [colorMode, manager]);

  return (
    <>
      <BokehFont />
      <ColorModeScript storageKey={bokehStorageKey} initialColorMode="system" type="localStorage" />
      <ThemeProvider theme={bokehTheme} colorModeManager={manager}>
        <ToastProvider>{children}</ToastProvider>
      </ThemeProvider>
    </>
  );
};

/**
 * Only renders children when theme is `bokeh-bokeh`.
 *
 * @deprecated Please import this component from `@vimeo/core/components/BokehOnly`
 */
export const BokehOnly = ({ children }: React.PropsWithChildren) => {
  const isBokeh = useIsBokeh();

  if (!isBokeh) return null;

  return <>{children}</>;
};

/**
 * Only renders children when theme is `bokeh-iris`.
 *
 * @deprecated Please import this component from @vimeo/core/components/IrisOnly
 */
export const IrisOnly = ({ children }: React.PropsWithChildren) => {
  const isBokeh = useIsBokeh();

  if (isBokeh) return null;

  return <>{children}</>;
};
